import AdminLayout from "@/components/layout/AdminLayout";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LayoutGrid, List, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { ArtworkDialog } from "@/components/artworks/ArtworkDialog";
import { ArtworkGridView } from "@/components/artworks/ArtworkGridView";
import { ArtworkListView } from "@/components/artworks/ArtworkListView";
import { SmartPagination } from "@/components/ui/smart-pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Command, CommandInput, CommandList, CommandEmpty } from "@/components/ui/command";

const ITEMS_PER_PAGE = 12;

export default function Artworks() {
  const [viewMode, setViewMode] = useState<"list" | "grid">("grid");
  const [titleQuery, setTitleQuery] = useState("");
  const [selectedCreatorId, setSelectedCreatorId] = useState<string>("all");
  const [selectedArtwork, setSelectedArtwork] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [creatorSearch, setCreatorSearch] = useState("");

  const { data: creators } = useQuery({
    queryKey: ["creators-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("creators")
        .select("creator_id, name, surname, nick_name")
        .order("name");

      if (error) throw error;
      return data || [];
    },
  });

  const { data: artworksData, isLoading } = useQuery({
    queryKey: ["artworks-list", currentPage, titleQuery, selectedCreatorId],
    queryFn: async () => {
      console.log("Fetching artworks...");
      let query = supabase
        .from("artworks")
        .select(`
          artwork_id,
          title,
          art_technique, 
          description,
          creation_date,
          location,
          verified,
          creator_id,
          preview,
          highres_file,
          creators (
            name,
            surname,
            nick_name
          ),
          editions (*)
        `, { count: 'exact' })
        .order('title');

      if (titleQuery) {
        query = query.ilike("title", `%${titleQuery}%`);
      }

      if (selectedCreatorId && selectedCreatorId !== "all") {
        query = query.eq("creator_id", parseInt(selectedCreatorId));
      }

      const from = (currentPage - 1) * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      query = query.range(from, to);

      const { data, error, count } = await query;

      if (error) {
        console.error("Error fetching artworks:", error);
        throw error;
      }

      console.log("Fetched artworks:", data, "total count:", count);
      return {
        items: data || [],
        totalCount: count || 0
      };
    },
  });

  const totalPages = Math.ceil((artworksData?.totalCount || 0) / ITEMS_PER_PAGE);

  const getArtistDisplayName = (creator: { name: string; surname: string; nick_name: string | null }) => {
    if (creator?.nick_name) {
      return `${creator.name} ${creator.surname} (${creator.nick_name})`;
    }
    return `${creator?.name} ${creator?.surname}`;
  };

  const handleArtworkClick = (artwork: any) => {
    setSelectedArtwork(artwork);
    setDialogOpen(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const handleCreatorChange = (creatorId: string) => {
    setSelectedCreatorId(creatorId);
    setCurrentPage(1);
  };

  const filteredCreators = creators?.filter(creator => {
    const searchTerm = creatorSearch.toLowerCase();
    const fullName = getArtistDisplayName(creator).toLowerCase();
    return fullName.includes(searchTerm);
  });

  const filteredArtworks = artworksData?.items || [];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold">Artworks</h1>
            <div className="flex gap-2">
              <Button
                variant={viewMode === "list" ? "default" : "outline"}
                size="icon"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "grid" ? "default" : "outline"}
                size="icon"
                onClick={() => setViewMode("grid")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by artwork title..."
                value={titleQuery}
                onChange={(e) => {
                  setTitleQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10"
              />
            </div>
            <Select
              value={selectedCreatorId}
              onValueChange={handleCreatorChange}
            >
              <SelectTrigger className="w-[250px]">
                <SelectValue placeholder="Select creator" />
              </SelectTrigger>
              <SelectContent>
                <Command>
                  <CommandInput 
                    placeholder="Search creators..."
                    value={creatorSearch}
                    onValueChange={setCreatorSearch}
                  />
                  <CommandList>
                    <CommandEmpty>No creators found.</CommandEmpty>
                    <SelectItem value="all">All creators</SelectItem>
                    {filteredCreators?.map((creator) => (
                      <SelectItem 
                        key={creator.creator_id} 
                        value={creator.creator_id.toString()}
                      >
                        {getArtistDisplayName(creator)}
                      </SelectItem>
                    ))}
                  </CommandList>
                </Command>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div>Loading artworks...</div>
        ) : viewMode === "list" ? (
          <ArtworkListView 
            artworks={filteredArtworks}
            onArtworkClick={handleArtworkClick}
            getArtistDisplayName={getArtistDisplayName}
          />
        ) : (
          <ArtworkGridView 
            artworks={filteredArtworks}
            onArtworkClick={handleArtworkClick}
            getArtistDisplayName={getArtistDisplayName}
          />
        )}

        {!isLoading && totalPages > 1 && (
          <SmartPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            className="justify-center"
          />
        )}

        {selectedArtwork && (
          <ArtworkDialog 
            artwork={selectedArtwork} 
            open={dialogOpen}
            onOpenChange={setDialogOpen}
          />
        )}
      </div>
    </AdminLayout>
  );
}
